import { Link } from "@heroui/react";
import { ExternalLink } from "lucide-react";

/**
 * Detects URLs in text and converts them to clickable links
 * Similar to WhatsApp's link detection
 */

interface LinkifyProps {
    text: string;
    className?: string;
}

// URL regex pattern - matches http, https, and www URLs
const URL_REGEX = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/gi;

export function Linkify({ text, className = "" }: LinkifyProps) {
    const parts: Array<{ type: 'text' | 'link'; content: string; url?: string }> = [];
    let lastIndex = 0;

    // Find all URLs in the text
    const matches = Array.from(text.matchAll(URL_REGEX));

    matches.forEach((match) => {
        const url = match[0];
        const index = match.index!;

        // Add text before the URL
        if (index > lastIndex) {
            parts.push({
                type: 'text',
                content: text.substring(lastIndex, index)
            });
        }

        // Add the URL
        let fullUrl = url;
        if (url.startsWith('www.')) {
            fullUrl = `https://${url}`;
        }

        parts.push({
            type: 'link',
            content: url,
            url: fullUrl
        });

        lastIndex = index + url.length;
    });

    // Add remaining text after last URL
    if (lastIndex < text.length) {
        parts.push({
            type: 'text',
            content: text.substring(lastIndex)
        });
    }

    // If no URLs found, return plain text
    if (parts.length === 0) {
        return <span className={className}>{text}</span>;
    }

    return (
        <span className={className}>
            {parts.map((part, index) => {
                if (part.type === 'link') {
                    return (
                        <Link
                            key={index}
                            href={part.url}
                            isExternal
                            showAnchorIcon
                            anchorIcon={<ExternalLink className="h-3 w-3" />}
                            className="text-primary underline hover:text-primary-600 transition-colors inline-flex items-center gap-1"
                            size="sm"
                        >
                            {part.content}
                        </Link>
                    );
                }
                return <span key={index}>{part.content}</span>;
            })}
        </span>
    );
}
